import React from 'react'
import {Text} from '../../../utils/chakraMonomorphic'

import copy from '../copy.json'
import {CoverageState} from '../coverageState'

interface Props {
  state: CoverageState
}

/**
 * The one place the page says what the recognition data can support.
 *
 * Rendered ONCE above both close-call tables rather than inside each: coverage
 * is a property of the data, not of either table, so per-table rendering
 * duplicated a three-sentence paragraph and the warning banner on one screen.
 *
 * Shown regardless of whether the tables have rows — a list built from partial
 * data is misleading whether it is empty or not.
 */
export const coverageNoteText = (state: CoverageState): string | null => {
  switch (state.kind) {
    case 'unknown':
      return copy.closeCalls.coverage.unknown
    case 'noManualTags':
      return copy.closeCalls.coverage.noManualTags
    case 'noTelemetry':
      return copy.closeCalls.coverage.noTelemetry.replace(
        '{total}',
        String(state.total),
      )
    case 'notACandidate':
      return copy.closeCalls.coverage.notACandidate
        .replace('{scanned}', String(state.scanned))
        .replace('{total}', String(state.total))
    case 'partial':
      return copy.closeCalls.coverage.partial
        .replace('{answerable}', String(state.answerable))
        .replace('{total}', String(state.total))
    case 'full':
    default:
      return null
  }
}

export const CoverageNote: React.FC<Props> = ({state}) => {
  const text = coverageNoteText(state)
  if (text === null) return null

  // `noManualTags` is an explanation, not a caveat — nothing is being
  // withheld or degraded, so it reads as ordinary body copy. The rest all
  // qualify a result the operator might otherwise act on.
  const isCaveat = state.kind !== 'noManualTags'

  return (
    <Text
      fontSize='sm'
      color={isCaveat ? 'attention.700' : 'gray.700'}
      mb={4}
      data-testid={`close-calls-coverage-${state.kind}`}>
      {text}
    </Text>
  )
}
